import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/adminApi";
import { fmtRelative, fmtDate } from "../lib/format";
import { Search, MoreVertical, Loader2, Users, Pencil, KeyRound, X, Check } from "lucide-react";
import RevealModal from "../components/RevealModal";

const ROLE_BADGE = {
  owner: { bg: "bg-[#7C5CFF]/10", text: "text-[#7C5CFF]" },
  manager: { bg: "bg-violet-50", text: "text-violet-700" },
  accountant: { bg: "bg-blue-50", text: "text-blue-700" },
  sales: { bg: "bg-emerald-50", text: "text-emerald-700" },
  warehouse: { bg: "bg-amber-50", text: "text-amber-700" },
};

export default function UsersPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 25;

  const [search, setSearch] = useState("");
  const [tenantFilter, setTenantFilter] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [tenantOptions, setTenantOptions] = useState([]);

  const [menuOpen, setMenuOpen] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);
  const [revealData, setRevealData] = useState(null);

  const debounceRef = useRef(null);

  // Load tenant options once
  useEffect(() => {
    api.get("/tenants", { params: { page_size: 200 } })
      .then((res) => setTenantOptions((res.data.items || []).map((t) => ({ id: t.tenant_id, name: t.name }))))
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (search.trim()) params.search = search.trim();
      if (tenantFilter) params.tenant_id = tenantFilter;
      if (roleFilter) params.role = roleFilter;
      const res = await api.get("/users", { params });
      setRows(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [page, search, tenantFilter, roleFilter]);

  useEffect(() => { load(); }, [load]);

  const handleSearchChange = (val) => {
    setSearch(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { setPage(1); }, 300);
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto glass-animate" data-testid="users-page">
      <div className="flex items-center gap-2 mb-1">
        <Users className="w-5 h-5 text-[#7C5CFF]" />
        <h1 className="text-2xl font-extrabold text-[#0B0F1A] tracking-tight">Users</h1>
      </div>
      <p className="text-sm text-[#5B617A] mb-5">{total} user{total !== 1 ? "s" : ""} across all tenants</p>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-2 h-9 bg-white/60 border border-[#7C5CFF]/[0.12] rounded-xl px-3 flex-1 min-w-[200px] max-w-sm backdrop-blur-sm">
          <Search className="w-4 h-4 text-[#5B617A] shrink-0" />
          <input
            data-testid="users-search"
            className="flex-1 bg-transparent text-sm text-[#0B0F1A] placeholder:text-[#5B617A]/50 focus:outline-none"
            placeholder="Search by name or email…"
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
        </div>
        <select
          data-testid="users-tenant-filter"
          value={tenantFilter}
          onChange={(e) => { setTenantFilter(e.target.value); setPage(1); }}
          className="h-9 bg-white/60 border border-[#7C5CFF]/[0.12] rounded-xl px-3 text-sm text-[#0B0F1A] focus:outline-none focus:border-[#7C5CFF] max-w-[200px] backdrop-blur-sm"
        >
          <option value="">All tenants</option>
          {tenantOptions.map((t) => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>
        <select
          data-testid="users-role-filter"
          value={roleFilter}
          onChange={(e) => { setRoleFilter(e.target.value); setPage(1); }}
          className="h-9 bg-white/60 border border-[#7C5CFF]/[0.12] rounded-xl px-3 text-sm text-[#0B0F1A] focus:outline-none focus:border-[#7C5CFF] backdrop-blur-sm"
        >
          <option value="">All roles</option>
          {["owner", "manager", "accountant", "sales", "warehouse"].map((r) => (
            <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="glass-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="users-table">
            <thead>
              <tr className="bg-white/40 border-b border-[#7C5CFF]/[0.08] text-left">
                <th className="px-3 py-2.5 text-[10px] font-semibold text-[#5B617A] uppercase tracking-wider">Name &amp; Email</th>
                <th className="px-3 py-2.5 text-[10px] font-semibold text-[#5B617A] uppercase tracking-wider">Tenant</th>
                <th className="px-3 py-2.5 text-[10px] font-semibold text-[#5B617A] uppercase tracking-wider">Role</th>
                <th className="px-3 py-2.5 text-[10px] font-semibold text-[#5B617A] uppercase tracking-wider">Created</th>
                <th className="px-3 py-2.5 text-[10px] font-semibold text-[#5B617A] uppercase tracking-wider">Last Login</th>
                <th className="px-3 py-2.5 text-[10px] font-semibold text-[#5B617A] uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && rows.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-12"><Loader2 className="w-5 h-5 animate-spin text-[#7C5CFF] mx-auto" /></td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-12 text-[#5B617A]">No users found.</td></tr>
              ) : rows.map((u) => {
                const rb = ROLE_BADGE[u.role] || ROLE_BADGE.sales;
                return (
                  <tr key={u.id} className="border-b border-[#7C5CFF]/[0.04] glass-row-hover transition-colors" data-testid={`user-row-${u.id}`}>
                    <td className="px-3 py-3">
                      <p className="font-semibold text-[#0B0F1A] truncate max-w-[180px]">{u.name}</p>
                      <p className="text-[10px] text-[#5B617A] truncate">{u.email}</p>
                      {u.must_change_password && (
                        <span className="inline-block mt-0.5 px-1.5 py-0.5 bg-amber-50 text-amber-700 text-[9px] font-bold rounded uppercase">Must reset</span>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      {u.tenant_id ? (
                        <Link to={`/tenants/${u.tenant_id}`} className="text-[#7C5CFF] text-xs font-semibold hover:underline truncate max-w-[140px] block" data-testid={`user-tenant-link-${u.id}`}>
                          {u.tenant_name || u.tenant_id}
                        </Link>
                      ) : <span className="text-xs text-[#5B617A]">—</span>}
                    </td>
                    <td className="px-3 py-3">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${rb.bg} ${rb.text}`}>
                        {u.role}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-[10px] text-[#5B617A]">{fmtDate(u.created_at)}</td>
                    <td className="px-3 py-3 text-[10px] text-[#5B617A]">{fmtRelative(u.last_login_at)}</td>
                    <td className="px-3 py-3 relative">
                      <button
                        data-testid={`user-actions-${u.id}`}
                        onClick={(e) => { e.stopPropagation(); setMenuOpen(menuOpen === u.id ? null : u.id); }}
                        className="p-1.5 rounded-lg hover:bg-[#7C5CFF]/[0.06] transition-colors"
                      >
                        <MoreVertical className="w-4 h-4 text-[#5B617A]" />
                      </button>
                      {menuOpen === u.id && (
                        <>
                          <div className="fixed inset-0 z-30" onClick={() => setMenuOpen(null)} />
                          <div className="absolute right-0 top-full mt-1 glass-dropdown z-40 min-w-[160px] py-1" data-testid="user-actions-dropdown">
                            <button
                              data-testid={`edit-user-${u.id}`}
                              onClick={() => { setEditTarget(u); setMenuOpen(null); }}
                              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[#0B0F1A] hover:bg-[#7C5CFF]/[0.06]"
                            >
                              <Pencil className="w-3.5 h-3.5" />Edit user
                            </button>
                            <button
                              data-testid={`reset-pw-${u.id}`}
                              onClick={() => { setResetTarget(u); setMenuOpen(null); }}
                              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[#0B0F1A] hover:bg-[#7C5CFF]/[0.06]"
                            >
                              <KeyRound className="w-3.5 h-3.5" />Force password reset
                            </button>
                          </div>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-[#7C5CFF]/[0.06]">
            <p className="text-xs text-[#5B617A]">Page {page} of {totalPages}</p>
            <div className="flex gap-1.5">
              <button disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="users-prev"
                className="px-3 py-1 rounded-lg text-xs font-semibold text-[#0B0F1A] bg-white/60 border border-[#7C5CFF]/[0.12] hover:bg-[#7C5CFF]/[0.06] disabled:opacity-40">Prev</button>
              <button disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="users-next"
                className="px-3 py-1 rounded-lg text-xs font-semibold text-[#0B0F1A] bg-white/60 border border-[#7C5CFF]/[0.12] hover:bg-[#7C5CFF]/[0.06] disabled:opacity-40">Next</button>
            </div>
          </div>
        )}
      </div>

      {/* Edit user modal */}
      {editTarget && <EditUserModal user={editTarget} onClose={() => setEditTarget(null)} onSaved={() => { setEditTarget(null); load(); }} />}

      {/* Password reset confirm */}
      {resetTarget && <ResetPasswordModal user={resetTarget} onClose={() => setResetTarget(null)} onRevealed={(data) => { setResetTarget(null); setRevealData(data); }} />}

      {/* One-shot reveal */}
      {revealData && (
        <RevealModal
          title="Temporary Password"
          subtitle={`${revealData.user?.name} — ${revealData.user?.email}`}
          fields={[
            { label: "Temporary Password", value: revealData.temporary_password, mono: true, copyable: true },
          ]}
          warning="This password will only be shown once. Share securely."
          onClose={() => setRevealData(null)}
        />
      )}
    </div>
  );
}

function EditUserModal({ user, onClose, onSaved }) {
  const [name, setName] = useState(user.name || "");
  const [email, setEmail] = useState(user.email || "");
  const [role, setRole] = useState(user.role || "sales");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const patch = {};
      if (name.trim() !== user.name) patch.name = name.trim();
      if (email.trim().toLowerCase() !== user.email) patch.email = email.trim().toLowerCase();
      if (role !== user.role) patch.role = role;
      if (Object.keys(patch).length === 0) { onClose(); return; }
      await api.patch(`/users/${user.id}`, patch);
      onSaved();
    } catch (err) {
      const d = err.response?.data?.detail;
      if (d && typeof d === "object" && d.code === "LAST_OWNER") {
        setError("This is the only owner. Promote another user first.");
      } else if (d && typeof d === "object" && d.code === "EMAIL_TAKEN") {
        setError("That email is already registered.");
      } else {
        setError(typeof d === "string" ? d : "Update failed");
      }
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm" onClick={onClose}>
      <div className="glass-modal w-full max-w-sm mx-4" onClick={(e) => e.stopPropagation()} data-testid="edit-user-modal">
        <div className="flex items-center justify-between p-5 border-b border-[#7C5CFF]/[0.08]">
          <p className="font-bold text-[#0B0F1A] text-sm">Edit User</p>
          <button onClick={onClose} data-testid="edit-user-close"><X className="w-4 h-4 text-[#5B617A]" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-3">
          {error && <p className="text-red-600 text-sm bg-red-50 p-2 rounded-xl" data-testid="edit-user-error">{error}</p>}
          <div>
            <label htmlFor="eu-name" className="block text-[10px] font-semibold text-[#5B617A] uppercase tracking-wider mb-1">Name</label>
            <input id="eu-name" data-testid="edit-user-name" value={name} onChange={(e) => setName(e.target.value)}
              className="w-full h-10 bg-white/60 border border-[#7C5CFF]/[0.12] rounded-xl px-3 text-sm text-[#0B0F1A] glass-input" />
          </div>
          <div>
            <label htmlFor="eu-email" className="block text-[10px] font-semibold text-[#5B617A] uppercase tracking-wider mb-1">Email</label>
            <input id="eu-email" data-testid="edit-user-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full h-10 bg-white/60 border border-[#7C5CFF]/[0.12] rounded-xl px-3 text-sm text-[#0B0F1A] glass-input" />
          </div>
          <div>
            <label htmlFor="eu-role" className="block text-[10px] font-semibold text-[#5B617A] uppercase tracking-wider mb-1">Role</label>
            <select id="eu-role" data-testid="edit-user-role" value={role} onChange={(e) => setRole(e.target.value)}
              className="w-full h-10 bg-white/60 border border-[#7C5CFF]/[0.12] rounded-xl px-3 text-sm text-[#0B0F1A] focus:outline-none focus:border-[#7C5CFF]">
              {["owner", "manager", "accountant", "sales", "warehouse"].map((r) => (
                <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>
              ))}
            </select>
          </div>
          <button data-testid="edit-user-submit" type="submit" disabled={saving}
            className="w-full h-10 rounded-xl text-white font-semibold text-sm flex items-center justify-center btn-primary-glass">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save changes"}
          </button>
        </form>
      </div>
    </div>
  );
}

function ResetPasswordModal({ user, onClose, onRevealed }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const confirm = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.post(`/users/${user.id}/reset-password`);
      onRevealed(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Reset failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm" onClick={onClose}>
      <div className="glass-modal w-full max-w-sm mx-4 p-5" onClick={(e) => e.stopPropagation()} data-testid="reset-pw-modal">
        <p className="font-bold text-[#0B0F1A] text-sm mb-2">Force Password Reset</p>
        <p className="text-sm text-[#5B617A] mb-3">
          Reset password for <strong>{user.email}</strong>? A temporary password will be generated.
        </p>
        {error && <p className="text-red-600 text-sm bg-red-50 p-2 rounded-xl mb-3" data-testid="reset-pw-error">{error}</p>}
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 h-10 rounded-xl border border-[#7C5CFF]/[0.12] bg-white/60 text-[#0B0F1A] font-semibold text-sm hover:bg-[#7C5CFF]/[0.06]">Cancel</button>
          <button data-testid="reset-pw-confirm" onClick={confirm} disabled={loading}
            className="flex-1 h-10 rounded-xl text-white font-semibold text-sm flex items-center justify-center btn-primary-glass">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Reset"}
          </button>
        </div>
      </div>
    </div>
  );
}
